import { yupResolver } from "@hookform/resolvers/yup";
import React from "react";
import { FormProvider, useFieldArray, useForm, useFormContext } from "react-hook-form";

import { DeleteButton } from "components/atoms/Buttons";
import {
  APPLICATION_FORM_NAMES,
  FormFieldConditionSchema,
  FormFieldSchema,
  formFieldSchema,
} from "components/templates/AddApplicationFormGroupModal/ApplicationForm";
import { CourseFormFieldApi } from "api";
import { useDictionaryValuesOptions } from "api/hooks";
import {
  PaziaTextarea,
  PaziaButton,
  PaziaMultiSelect,
  PaziaCheckbox,
  PaziaDatepicker,
  PaziaInput,
  PaziaSelect,
  IDropdownOption,
  PaziaFormButton,
} from "components/atoms";
import { FORM_INPUT_OPTIONS, COURSE_FORM_FIELD_TYPES__DATES_TYPES, FIELD_CONDITIONS_POSSIBLE_OPTIONS } from "consts";
import { COURSE_FORM_FIELD_TYPES } from "consts";
import { useIsMobile } from "utils/hooks/useIsMobile";

interface ApplicationFieldFormProps {
  field?: CourseFormFieldApi;
  onSubmit: (data: FormFieldSchema) => void;
  dictionaries: IDropdownOption[];
  allFields: CourseFormFieldApi[];
  onCancel: () => void;
}
export const ApplicationFieldForm = ({
  field,
  onSubmit,
  allFields,
  dictionaries,
  onCancel,
}: ApplicationFieldFormProps) => {
  const methods = useForm({
    resolver: yupResolver(formFieldSchema),
    defaultValues: {
      [APPLICATION_FORM_NAMES.FIELDS_ID]: field?.id,
      [APPLICATION_FORM_NAMES.FIELDS_NAME]: field?.name,
      [APPLICATION_FORM_NAMES.FIELDS_DESCRIPTION]: field?.description,
      [APPLICATION_FORM_NAMES.FIELDS_FIELDTYPE]: field?.fieldType,
      [APPLICATION_FORM_NAMES.FIELDS_DICTIONARY]: field?.dictionaryId,
      [APPLICATION_FORM_NAMES.IS_REQUIRED]: field?.isRequired,
      [APPLICATION_FORM_NAMES.FIELDS_CONDITIONS]: field?.conditions,
    },
  });
  const { fields, append, remove } = useFieldArray({
    control: methods.control,
    name: APPLICATION_FORM_NAMES.FIELDS_CONDITIONS,
  });
  const isDictionarySelected =
    methods.watch(APPLICATION_FORM_NAMES.FIELDS_FIELDTYPE) === COURSE_FORM_FIELD_TYPES.DICTIONARY;

  const onSubmitHandler = (data: FormFieldSchema) => {
    const request = {
      ...data,
      conditions: data.conditions?.map((x) => ({
        ...x,
        conditionValue: Array.isArray(x.conditionValue) ? x.conditionValue.join(",") : x.conditionValue,
      })),
    };
    onSubmit(request);
    methods.reset();
  };

  const { handleSubmit } = methods;
  return (
    <>
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onSubmitHandler)} className="flex flex-col gap-2">
          <div className="mb-4 grid grid-cols-1 items-end gap-2 sm:grid-cols-12">
            <PaziaSelect
              name={APPLICATION_FORM_NAMES.FIELDS_FIELDTYPE}
              options={FORM_INPUT_OPTIONS}
              label="Typ"
              className="sm:col-span-4"
              required
              hideErrors
            />
            <div className="sm:col-span-4">
              <PaziaInput name={APPLICATION_FORM_NAMES.FIELDS_NAME} label="Nazwa" required hideErrors />
            </div>
            <div className="sm:col-span-4">
              {isDictionarySelected && (
                <PaziaSelect
                  required
                  label="Słownik"
                  name={APPLICATION_FORM_NAMES.FIELDS_DICTIONARY}
                  options={dictionaries}
                />
              )}
            </div>
            <div className="sm:col-span-12">
              <PaziaCheckbox name={APPLICATION_FORM_NAMES.IS_REQUIRED} label="Wymagane" />
            </div>
            <div className="sm:col-span-12">
              <PaziaTextarea name={APPLICATION_FORM_NAMES.FIELDS_DESCRIPTION} label="Opis" />
            </div>
          </div>
          <div>
            <div className="text-center text-sm font-semibold text-primary-500">Warunki widoczności (ukryte gdy)</div>

            {fields?.length < 1 ? (
              <div className="text-center text-sm italic text-slate-500">
                Pole jest widoczne zawsze, dodaj warunki widoczności w celu modyfikacji.
              </div>
            ) : (
              fields.map((field, index) => (
                <ConditionRow key={field.id} index={index} onRemove={() => remove(index)} allFields={allFields} />
              ))
            )}
          </div>
          <div className="flex justify-end">
            <PaziaButton title="Dodaj warunek" type="tertiary" onClick={() => append({} as FormFieldConditionSchema)} />
          </div>
          <div className="ml-auto mt-4 flex gap-2">
            <PaziaButton title="Anuluj" type="tertiary" btnType="button" onClick={onCancel} />
            <PaziaFormButton />
          </div>
        </form>
      </FormProvider>
    </>
  );
};

interface ConditionRowProps {
  index: number;
  allFields: CourseFormFieldApi[];
  onRemove: () => void;
}
const ConditionRow = ({ index, allFields, onRemove }: ConditionRowProps) => {
  const { watch } = useFormContext();
  const fieldId = watch(
    `${APPLICATION_FORM_NAMES.FIELDS_CONDITIONS}[${index}].${APPLICATION_FORM_NAMES.CONDITIONS_FIELD}`,
  );
  const field = allFields.find((x) => x.id == fieldId);
  // Below sm the row stacks into its own bordered block, so every row needs its labels (not only the first)
  const isPhone = useIsMobile("(max-width: 639px)");
  const showLabels = index === 0 || isPhone;

  return (
    <div className="mb-2 grid grid-cols-1 gap-2 rounded-lg border border-slate-200 p-2 dark:border-slate-700 sm:grid-cols-12 sm:rounded-none sm:border-0 sm:p-0">
      <PaziaSelect
        label={(showLabels && ("Pole" as string)) || ""}
        className="sm:col-span-4"
        name={`${APPLICATION_FORM_NAMES.FIELDS_CONDITIONS}[${index}].${APPLICATION_FORM_NAMES.CONDITIONS_FIELD}`}
        options={allFields.map((x) => ({ value: x.id, label: x.name }))}
      />
      <PaziaSelect
        label={(showLabels && ("Jest" as string)) || ""}
        className="sm:col-span-3"
        name={`${APPLICATION_FORM_NAMES.FIELDS_CONDITIONS}[${index}].${APPLICATION_FORM_NAMES.CONDITIONS_CONDITION}`}
        options={
          FIELD_CONDITIONS_POSSIBLE_OPTIONS[field?.fieldType as keyof typeof FIELD_CONDITIONS_POSSIBLE_OPTIONS] ??
          FIELD_CONDITIONS_POSSIBLE_OPTIONS[COURSE_FORM_FIELD_TYPES.TEXT] ??
          []
        }
      />
      <ConditionValueInput
        name={`${APPLICATION_FORM_NAMES.FIELDS_CONDITIONS}[${index}].${APPLICATION_FORM_NAMES.CONDITIONS_CONDITION_VALUE}`}
        label={(showLabels && ("Wartość" as string)) || ""}
        field={field}
      />
      {field && (
        <PaziaInput
          name={`${APPLICATION_FORM_NAMES.FIELDS_CONDITIONS}[${index}].${APPLICATION_FORM_NAMES.CONDITIONS_FIELD_TYPE}`}
          hidden
          value={+field.fieldType}
        />
      )}

      <div className="flex items-end justify-end">
        <DeleteButton onClick={onRemove} />
      </div>
    </div>
  );
};

interface ConditionValueInputProps {
  name: string;
  label: string;
  field: CourseFormFieldApi | undefined;
}
const ConditionValueInput = React.memo(({ name, label, field }: ConditionValueInputProps) => {
  if (!field) return <div className="hidden sm:col-span-4 sm:block"> </div>;
  if (field.fieldType === COURSE_FORM_FIELD_TYPES.DICTIONARY && field.dictionaryId)
    return <DictionaryConditionValueInput name={name} label={label} dictionaryId={field.dictionaryId} />;
  if (field.fieldType === COURSE_FORM_FIELD_TYPES.BOOLEAN)
    return (
      <PaziaSelect
        name={name}
        label={label}
        className="sm:col-span-4"
        options={[
          { value: "1", label: "TAK" },
          { value: "0", label: "NIE" },
        ]}
      />
    );
  if (COURSE_FORM_FIELD_TYPES__DATES_TYPES.includes(field.fieldType))
    return <PaziaDatepicker name={name} label={label} className="sm:col-span-4" />;

  return <PaziaInput name={name} label={label} className="sm:col-span-4" />;
});

interface DictionaryConditionValueInputProps {
  dictionaryId: number;
  name: string;
  label: string;
}

const DictionaryConditionValueInput = ({ name, label, dictionaryId }: DictionaryConditionValueInputProps) => {
  const { setValue } = useFormContext();
  const { loading, options } = useDictionaryValuesOptions(dictionaryId);

  const onChange = (values: number[]) => {
    const selectedOptions = options.filter((x) => values.includes(+x.value));
    setValue(name + "Label", selectedOptions);
  };

  return loading ? null : (
    <PaziaMultiSelect name={name} label={label} className="sm:col-span-4" options={options ?? []} onChange={onChange} />
  );
};
